import { z } from "zod";
import { GeneratedInsight } from "./insight";
import {
  isActiveKpi,
  kpiImprovement,
  kpiSubjectId,
  meetsKpiTarget,
  resolveKpisForTeam,
  resolveMemberKpiTarget,
  resolveTeamKpiTarget,
  hasPeriodDependentTarget,
  resolvePeriodTarget,
  type KpiDefinition,
  type KpiEntryRecord,
  type KpiSubjectKind,
  type RoleKpiTarget,
} from "./kpi";
import { resolveKpiValue } from "./kpi-formula";
import { canonicalCrmToolkitSlug } from "./crm-identity";
import {
  OBJECTIVE_OFF_TRACK_MISSES,
  kpiValueTrend,
  kpiValueForPeriod,
  type ObjectiveProgressStatus,
  type ObjectiveTrend,
} from "./playbook-progress";

/**
 * Pure KPI status scoring, per KPI per subject (team totals and individual
 * members), computed nightly and persisted as a single snapshot document so
 * the dashboard, MCP tools, and the `kpi-status` rule generator all read one
 * cheap doc instead of re-deriving trends from entries.
 *
 * Stored at: Businesses/{businessId}/kpiStatus/current
 *
 * Deliberately reuses the playbook-progress status ladder and trend maths —
 * an objective's "off track" and a KPI's "off track" must mean the same thing
 * or the two surfaces will contradict each other on the same numbers.
 */

export const KPI_STATUS_GENERATOR_KEY = "kpi-status";

/** Periods of history a snapshot reasons over (and carries as sparkline points). */
export const KPI_STATUS_PERIODS = 8;

export type KpiSubjectStatus = ObjectiveProgressStatus;
export type KpiSubjectTrend = ObjectiveTrend;

export interface KpiStatusRow {
  kpiId: string;
  teamId: string;
  subjectKind: KpiSubjectKind;
  /** `kpiSubjectId` form — member id, or `team:{teamId}`. */
  subjectId: string;
  memberId: string | null;
  /** Latest recorded value (the current period's running total when present). */
  currentValue: number | null;
  /** Member rows: role target scaled to the period; team rows: flat target. */
  target: number | null;
  /** Judged on the latest ENDED period — the current one is always partial. */
  meets: boolean | null;
  trend: KpiSubjectTrend;
  status: KpiSubjectStatus;
  /** `kpiImprovement` between the two latest ended scored periods. */
  improvement: number | null;
  /** One value per snapshot period, oldest first; null = nothing recorded. */
  points: (number | null)[];
  /**
   * One target per point, when the target changes per period (GRA-85: last
   * year + X %, or per day). `target` is then the newest known one.
   */
  targets?: (number | null)[];
  /**
   * Member rows of a KPI read from a company CRM, when this member is not
   * linked to a CRM user for it (GRA-56). An empty value then means "we
   * cannot tell whose deals are theirs", not "nothing happened", and the UI
   * says so. Absent on snapshots written before GRA-56.
   */
  notLinkedToCrm?: boolean;
  /**
   * Team rows of a per-seller company-CRM KPI: how many of the team's sellers
   * are not linked to a CRM user. They count towards the team target but add
   * nothing to the total, so the total falls short for a reason (GRA-62).
   */
  membersNotLinkedToCrm?: number;
  /**
   * Team rows only: the running (not yet ended) period, when the nightly
   * autofill has a value for it. The dashboard leads with this; everything
   * else above stays about ended periods, so insights and MCP tools keep
   * judging finished weeks only.
   */
  live?: KpiLiveReading;
}

/** How a running period's total compares with where it should be by now. */
export type KpiPace = "on_pace" | "behind";

export interface KpiLiveReading {
  periodStart: string;
  /** Exclusive, like every period end. */
  periodEnd: string;
  /** The day the reading was taken; it covers the period up to that morning. */
  asOf: string;
  value: number;
  /**
   * What the value should be by now: the target scaled to the workdays that
   * have passed for a summed KPI, the target itself for a ratio. Null when the
   * KPI is not judged mid-period (no target, a target that changes per period,
   * or no workday has passed yet).
   */
  expected: number | null;
  pace: KpiPace | null;
  /** The previous period's running total on the same day; null until recorded. */
  previousAtSamePoint: number | null;
}

/** Running team totals of one period, by day: `days[asOf][teamId][kpiId]`. */
export interface KpiProgressRecord {
  periodStart: string;
  periodEnd: string;
  days: Record<string, Record<string, Record<string, number>>>;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function isoDayMs(iso: string): number {
  return Date.parse(`${iso.slice(0, 10)}T00:00:00.000Z`);
}

/** Monday-to-Friday days in [start, endExclusive). */
export function countWorkdays(start: string, endExclusive: string): number {
  let count = 0;
  for (let ms = isoDayMs(start); ms < isoDayMs(endExclusive); ms += DAY_MS) {
    const day = new Date(ms).getUTCDay();
    if (day !== 0 && day !== 6) count += 1;
  }
  return count;
}

/**
 * Whether a KPI's total grows through the period, so its target is reached
 * bit by bit and can be paced. Ratios (calculated KPIs, percentages) are not:
 * their running value is judged against the target as is.
 */
export function isSummedKpi(def: Pick<KpiDefinition, "source" | "unit">): boolean {
  return def.source.kind !== "calculated" && def.unit.trim() !== "%";
}

/**
 * The value a running period should have reached by `asOf`. Workdays only:
 * a sales team books nothing on a Sunday, and a calendar-day pace would call
 * every Monday a miss.
 */
export function kpiPaceExpected(
  def: Pick<KpiDefinition, "source" | "unit" | "targetKind" | "targetBasis">,
  target: number | null,
  period: { periodStart: string; periodEnd: string },
  asOf: string,
): number | null {
  if (target == null || hasPeriodDependentTarget(def)) return null;
  if (!isSummedKpi(def)) return target;
  const total = countWorkdays(period.periodStart, period.periodEnd);
  // The reading is taken in the morning: today has not happened yet.
  const elapsed = countWorkdays(
    period.periodStart,
    asOf < period.periodEnd ? asOf : period.periodEnd,
  );
  if (total === 0 || elapsed === 0) return null;
  return (target * elapsed) / total;
}

/**
 * The live reading for one team row, or null when the running period has no
 * value for it. Store-report KPIs are left out: their numbers are reported
 * per closed day and keep the ended-period view.
 */
export function kpiLiveReading(input: {
  def: KpiDefinition;
  definitions: KpiDefinition[];
  teamId: string;
  /** The row's team target, as `computeKpiStatusRows` resolved it. */
  target: number | null;
  period: { periodStart: string; periodEnd: string };
  asOf: string;
  /** Entries recorded for the running period. */
  entries: KpiEntryRecord[];
  previousAtSamePoint: number | null;
}): KpiLiveReading | null {
  if (input.def.source.kind === "upload") return null;
  const value =
    kpiValueForPeriod(input.def.id, input.teamId, input.entries, input.definitions)
      ?.value ?? null;
  if (value == null) return null;
  const expected = kpiPaceExpected(input.def, input.target, input.period, input.asOf);
  const met =
    expected != null
      ? meetsKpiTarget({ direction: input.def.direction, target: expected }, value)
      : null;
  return {
    periodStart: input.period.periodStart,
    periodEnd: input.period.periodEnd,
    asOf: input.asOf,
    value,
    expected,
    pace: met == null ? null : met ? "on_pace" : "behind",
    previousAtSamePoint: input.previousAtSamePoint,
  };
}

export interface KpiStatusSnapshot {
  businessId: string;
  /** The newest period the snapshot covers (the current check-in period). */
  periodStart: string;
  periodEnd: string;
  computedAt: string;
  rows: KpiStatusRow[];
}

export const GetKpiStatusInput = z.object({
  businessId: z.string().min(1),
  /** Omit for every visible row; pass to scope to one team. */
  salesTeamId: z.string().min(1).optional(),
});
export type GetKpiStatusInput = z.infer<typeof GetKpiStatusInput>;

export interface KpiStatusMemberInput {
  id: string;
  salesTeamId: string | null;
  roleKpis: RoleKpiTarget[];
  /**
   * Canonical slugs of the company CRMs this member has no CRM identity for
   * (see `canonicalCrmToolkitSlug`). Which toolkits count as company CRMs is
   * the caller's call — this package does not know the toolkit registry.
   */
  unlinkedCrmToolkits?: string[];
}

/** Shared status ladder — same thresholds as `objectiveProgress`. */
function statusOf(
  judged: { met: boolean | null }[],
  scoredCount: number,
): KpiSubjectStatus {
  if (scoredCount === 0) return "no_data";
  if (judged.length === 0) return "no_target";
  const recent = judged.slice(-OBJECTIVE_OFF_TRACK_MISSES);
  const latest = judged[judged.length - 1]!;
  if (
    recent.length >= OBJECTIVE_OFF_TRACK_MISSES &&
    recent.every((p) => p.met === false)
  ) {
    return "off_track";
  }
  return latest.met === true ? "on_track" : "stalling";
}

function scoreSeries(
  def: KpiDefinition,
  target: number | null,
  points: (number | null)[],
  currentIndex: number | null,
  /**
   * One target per point when it changes per period (GRA-85: last year + X %,
   * or a per-day target over the days with data). Overrides `target`.
   */
  targetsPerPoint?: (number | null)[],
): Pick<
  KpiStatusRow,
  "currentValue" | "meets" | "trend" | "status" | "improvement"
> {
  const recorded = points.filter((v): v is number => v != null);
  const endedIndexes = points
    .map((_, i) => i)
    .filter((i) => i !== currentIndex && points[i] != null);
  const scored = endedIndexes.map((i) => points[i]!);
  const targetAt = (i: number) => (targetsPerPoint ? (targetsPerPoint[i] ?? null) : target);
  const judged = endedIndexes
    .filter((i) => targetAt(i) != null)
    .map((i) => ({
      met: meetsKpiTarget({ direction: def.direction, target: targetAt(i)! }, points[i]!),
    }));
  const latestIndex = endedIndexes.length > 0 ? endedIndexes[endedIndexes.length - 1]! : null;
  const latestScored = latestIndex != null ? points[latestIndex]! : null;
  const latestTarget = latestIndex != null ? targetAt(latestIndex) : null;
  return {
    currentValue: recorded.length > 0 ? recorded[recorded.length - 1]! : null,
    meets:
      latestScored != null && latestTarget != null
        ? meetsKpiTarget({ direction: def.direction, target: latestTarget }, latestScored)
        : null,
    trend: kpiValueTrend(scored, def),
    status: statusOf(judged, scored.length),
    improvement:
      scored.length >= 2
        ? kpiImprovement(def, scored[scored.length - 2]!, latestScored!)
        : null,
  };
}

/**
 * Status rows for every (active KPI, subject) pair in the business.
 *
 * Team rows use `kpiValueForPeriod` (recorded team total preferred over the
 * member sum — same semantics as the webapp charts) against the flat
 * definition target. Member rows read the member's own entries against the
 * role-scaled target (`resolveMemberKpiTarget`) when `periodDays` is known,
 * else the flat target.
 *
 * `periodStarts` must be oldest first; `points` come back in the same order.
 * When `currentPeriodStart` names one of them, that still-running period is
 * kept in `points` and `currentValue` but excluded from meets/trend/status/
 * improvement — a partially filled period must not read as a miss.
 */
export function computeKpiStatusRows(input: {
  definitions: KpiDefinition[];
  entries: KpiEntryRecord[];
  periodStarts: string[];
  teams: { id: string; parentTeamId?: string | null }[];
  members: KpiStatusMemberInput[];
  periodDays: number | null;
  currentPeriodStart?: string | null;
  /**
   * Grocery store (GRA-97): KPIs belong to the store and its departments,
   * never to a person. Departments inherit the store's KPIs along
   * `parentTeamId`, and no member rows are produced.
   */
  teamOnly?: boolean;
}): KpiStatusRow[] {
  const rows: KpiStatusRow[] = [];
  const currentIndexRaw =
    input.currentPeriodStart != null
      ? input.periodStarts.indexOf(input.currentPeriodStart)
      : -1;
  const currentIndex = currentIndexRaw >= 0 ? currentIndexRaw : null;
  const entriesByPeriod = new Map<string, KpiEntryRecord[]>();
  for (const entry of input.entries) {
    const group = entriesByPeriod.get(entry.periodStart);
    if (group) group.push(entry);
    else entriesByPeriod.set(entry.periodStart, [entry]);
  }

  for (const team of input.teams) {
    const defs = resolveKpisForTeam(
      input.definitions,
      team.id,
      input.teamOnly
        ? {
            inheritFromParents: input.teams.map((t) => ({
              id: t.id,
              parentTeamId: t.parentTeamId ?? null,
            })),
          }
        : undefined,
    ).filter(isActiveKpi);
    if (defs.length === 0) continue;
    const teamMembers = input.members.filter((m) => m.salesTeamId === team.id);

    for (const def of defs) {
      const teamPoints = input.periodStarts.map((periodStart) => {
        const forPeriod = entriesByPeriod.get(periodStart) ?? [];
        return (
          kpiValueForPeriod(def.id, team.id, forPeriod, input.definitions)
            ?.value ?? null
        );
      });
      // Targets that change per period (GRA-85) are worked out per point
      // from the same period last year, or the days with report data.
      const teamTargets = hasPeriodDependentTarget(def)
        ? input.periodStarts.map((periodStart) => {
            const entry = (entriesByPeriod.get(periodStart) ?? []).find(
              (e) => e.subjectKind === "team" && e.teamId === team.id && e.values[def.id] != null,
            );
            return entry
              ? resolvePeriodTarget(def, {
                  lastYear: entry.lastYearValues?.[def.id] ?? null,
                  days: entry.measureDays?.[def.id] ?? null,
                })
              : null;
          })
        : undefined;
      // Per-seller targets add up to the team's (GRA-62).
      const flatTeamTarget = resolveTeamKpiTarget(def, teamMembers, input.periodDays);
      const teamTarget = teamTargets
        ? ([...teamTargets].reverse().find((t) => t != null) ?? null)
        : flatTeamTarget;
      const isNotLinked = (m: KpiStatusMemberInput) =>
        def.source.kind === "integration" &&
        def.source.attribution !== "team" &&
        (m.unlinkedCrmToolkits ?? []).includes(
          canonicalCrmToolkitSlug(def.source.toolkitSlug),
        );
      const notLinkedCount = teamMembers.filter(isNotLinked).length;
      rows.push({
        kpiId: def.id,
        teamId: team.id,
        subjectKind: "team",
        subjectId: kpiSubjectId("team", team.id),
        memberId: null,
        target: teamTarget,
        points: teamPoints,
        ...(teamTargets ? { targets: teamTargets } : {}),
        ...scoreSeries(def, teamTarget, teamPoints, currentIndex, teamTargets),
        ...(notLinkedCount > 0 ? { membersNotLinkedToCrm: notLinkedCount } : {}),
      });

      for (const member of input.teamOnly ? [] : teamMembers) {
        const memberPoints = input.periodStarts.map((periodStart) => {
          const forPeriod = entriesByPeriod.get(periodStart) ?? [];
          const entry = forPeriod.find(
            (e) => e.subjectKind === "member" && e.memberId === member.id,
          );
          // Resolved here rather than trusting the caller to have enriched the
          // entries: this function is pure and is fed straight from Firestore
          // in tests and generators alike. For a calculated KPI that means the
          // person's own recorded numbers only — never the team's.
          const resolved = resolveKpiValue(
            def.id,
            input.definitions,
            (kpiId) => entry?.values[kpiId],
          );
          return resolved.ok ? resolved.value : null;
        });
        const target = resolveMemberKpiTarget(
          def,
          member.roleKpis,
          input.periodDays,
        );
        const notLinkedToCrm = isNotLinked(member);
        rows.push({
          kpiId: def.id,
          teamId: team.id,
          subjectKind: "member",
          subjectId: kpiSubjectId("member", member.id),
          memberId: member.id,
          target,
          points: memberPoints,
          ...scoreSeries(def, target, memberPoints, currentIndex),
          ...(notLinkedToCrm ? { notLinkedToCrm: true } : {}),
        });
      }
    }
  }
  return rows;
}

/**
 * Deterministic Swedish KPI insights from the snapshot's team rows: an
 * off-target warning per (KPI, team) that has slipped, and a recognition when
 * a KPI both meets its target and trends up. Member rows never alert — a
 * nightly robot flagging individuals by name is noise, not insight; managers
 * see member statuses on the dashboard instead.
 *
 * Reconcile expiry: keyed `kpi-status:off:{kpiId}:{teamId}` /
 * `kpi-status:up:{kpiId}:{teamId}`, so a row stays active while the condition
 * holds and expires the night it stops. KPIs in `excludeKpiIds` (linked to an
 * active playbook objective) are skipped — `playbook-progress` already alarms
 * on those and two alarms for one number is one too many.
 */
export function buildKpiStatusInsights(input: {
  rows: KpiStatusRow[];
  definitions: KpiDefinition[];
  teams: { id: string; name: string }[];
  excludeKpiIds?: Set<string>;
}): GeneratedInsight[] {
  const out: GeneratedInsight[] = [];
  const teamRows = input.rows.filter((r) => r.subjectKind === "team");

  for (const row of teamRows) {
    if (input.excludeKpiIds?.has(row.kpiId)) continue;
    const def = input.definitions.find((d) => d.id === row.kpiId);
    const team = input.teams.find((t) => t.id === row.teamId);
    if (def == null || team == null) continue;
    const unit = def.unit ? ` ${def.unit}` : "";

    if (row.status === "off_track" && row.target != null) {
      out.push(
        GeneratedInsight.parse({
          generatorKey: KPI_STATUS_GENERATOR_KEY,
          dedupeKey: `${KPI_STATUS_GENERATOR_KEY}:off:${row.kpiId}:${row.teamId}`,
          source: "rule",
          scope: "team",
          salesTeamId: row.teamId,
          severity: "warning",
          accent: "warning",
          ctaVariant: "button",
          icon: "TrendingDown",
          title: `${def.name} ligger under målet i ${team.name}`,
          description:
            `${def.name} har missat målet ${row.target}${unit} minst ` +
            `${OBJECTIVE_OFF_TRACK_MISSES} perioder i rad` +
            (row.currentValue != null
              ? ` (senast ${row.currentValue}${unit}).`
              : ".") +
            ` Dags att titta på vad som bromsar.`,
          cta: { label: "Öppna analys", to: "/coach/performance" },
          starterLabel: "Varför missar vi målet?",
          suggestedPrompt:
            `Varför ligger ${def.name} under målet i ${team.name}, ` +
            `och vad kan vi göra åt det?`,
          priority: 65,
          confidence: 1,
        }),
      );
    } else if (row.status === "on_track" && row.trend === "improving") {
      out.push(
        GeneratedInsight.parse({
          generatorKey: KPI_STATUS_GENERATOR_KEY,
          dedupeKey: `${KPI_STATUS_GENERATOR_KEY}:up:${row.kpiId}:${row.teamId}`,
          source: "rule",
          scope: "team",
          salesTeamId: row.teamId,
          severity: "info",
          accent: "primary",
          ctaVariant: "link",
          icon: "TrendingUp",
          title: `${def.name} över målet och stigande i ${team.name}`,
          description:
            `${def.name} når målet` +
            (row.target != null ? ` ${row.target}${unit}` : "") +
            ` och trenden pekar uppåt` +
            (row.currentValue != null
              ? ` (senast ${row.currentValue}${unit}).`
              : ".") +
            ` Värt att uppmärksamma teamet.`,
          cta: { label: "Se trenden", to: "/coach/performance" },
          starterLabel: "Vad driver förbättringen?",
          suggestedPrompt:
            `Vad ligger bakom förbättringen av ${def.name} i ${team.name}, ` +
            `och hur behåller vi den?`,
          priority: 35,
          confidence: 1,
        }),
      );
    }
  }
  return out;
}
